import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faLock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { VideoWizard } from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { isStepApproved, isStepLocked, STEP_KEYS, stepOf } from "./wizard.utils";

interface WizardStepperProps {
	wizard: VideoWizard;
	currentStep: number;
	onSelect: (step: number) => void;
}

/**
 * The five steps with their per-step cost line. Both the lock state and every figure come
 * from the server — a step is walkable because `unlockedStep` says so, never because the
 * client worked it out.
 */
export const WizardStepper: FunctionComponent<WizardStepperProps> = ({
	wizard,
	currentStep,
	onSelect,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	return (
		<nav className="flex flex-wrap gap-2 border-b border-border-soft bg-white px-5 py-3">
			{STEP_KEYS.map((key, index) => {
				const step = index + 1;
				const state = stepOf(wizard, step);
				const approved = isStepApproved(wizard, step);
				const locked = isStepLocked(wizard, step);
				const active = currentStep === step;
				const actualCostUsd = state?.actualCostUsd ?? 0;
				// A job in flight for this step: the chip must agree with the pane beside it
				// instead of reading "not started" through an 80-second generation.
				const running = (wizard.activeJobs ?? []).some((job) => job.step === step);

				return (
					<button
						key={key}
						className={classNames(
							"flex min-w-[168px] flex-1 items-center gap-3 rounded-card border px-3 py-2 text-left transition-colors",
							active ? "border-accent bg-accent-tint" : "border-border bg-white",
							locked ? "cursor-not-allowed opacity-60" : "hover:border-accent"
						)}
						disabled={locked}
						title={locked ? ts("stepper.lockedHint") : undefined}
						type="button"
						onClick={() => onSelect(step)}
					>
						<span
							className={classNames(
								"flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold",
								approved ? "bg-success-light text-text" : "bg-surface text-subtle"
							)}
						>
							{approved ? <Icon icon={faCheck} /> : locked ? <Icon icon={faLock} /> : step}
						</span>
						<span className="min-w-0">
							<span className="block truncate text-[13px] font-bold text-ink">
								{step} · {ts(`steps.${key}.title`)}
							</span>
							{/* The step's own status, not its price. Every cost figure in the wizard
							    now lives in the rail, so the same number is not quoted in four
							    places and cannot disagree with itself.
							    Only a job in flight reads as running. A locked step says which
							    step it waits for — `unlockedStep`, the first one not approved —
							    and a started one that it awaits approval: "in Arbeit" on a
							    locked step read as an export running for three weeks
							    (BCM360-373). */}
							<span
								className={classNames(
									"block text-xs font-semibold",
									approved
										? "text-success"
										: running || (!locked && actualCostUsd > 0)
											? "text-warning-ink"
											: "text-subtle"
								)}
							>
								{approved
									? ts("stepper.approved")
									: running
										? ts("stepper.running")
										: locked
											? ts("stepper.waitingFor", { step: wizard.unlockedStep })
											: actualCostUsd > 0
												? ts("stepper.awaitingApproval")
												: ts("stepper.pending")}
							</span>
						</span>
					</button>
				);
			})}
		</nav>
	);
};
